import { supabase } from '@/lib/supabase';
import {
  beginCallUiFromApp,
  getWebRTCState,
  mergeOutboundDestinationPhone,
  setActiveCrmCall,
  setDialContext,
} from '@/lib/telnyxWebRTCStore';
import type { TelnyxWebRTCState } from '@/lib/telnyxWebRTCStore';

/** WebRTC ya tiene pata real: no usar `setDialContext` (resetea `activeCallId` a `pending`). */
function shouldMergeDialDestinationOnly(): boolean {
  const s = getWebRTCState();
  const id = s.activeCallId;
  return s.callPhase !== 'none' && id != null && id !== '' && id !== 'pending';
}

/**
 * **Un solo “teléfono” WebRTC** para salientes: manual (FAB/marcador) o automático (Realtime tras INSERT `crm_calls`).
 * Mismo `telnyxWebRTCStore`, mismos presets de timbrado (`callRingbackLoop` / `ringbackAudio`), misma fase UI.
 * Solo cambia el disparador (clic vs evento).
 */
export function syncWebRtcStoreFromOutboundCrmInsert(options: {
  destinationPhone: string | null | undefined;
  crmCallId?: string | null;
  crmStatus?: string | null;
}): void {
  const dest = options.destinationPhone?.trim() || null;
  /**
   * INSERT `crm_calls` desde auto-llamada es siempre saliente en negocio.
   * `forceBusinessOutbound: true` aunque `destination_phone` venga vacío un tick (RLS/replica),
   * para que `deriveCallUiPhaseWithCrm` no pase a “active” antes del `answered` del PSTN.
   */
  if (dest) {
    if (shouldMergeDialDestinationOnly()) {
      mergeOutboundDestinationPhone(dest);
    } else {
      setDialContext(dest);
    }
  } else if (!shouldMergeDialDestinationOnly()) {
    setDialContext(null);
    beginCallUiFromApp();
  }
  if (options.crmCallId) {
    setActiveCrmCall(options.crmCallId, options.crmStatus ?? 'initiated', {
      forceBusinessOutbound: true,
    });
  }
}

/**
 * Tras `call-center-start-call` exitoso, guarda el número en el store WebRTC
 * para la tarjeta de llamada y el tono de timbrado.
 * `crmCallId` enlaza `crm_calls` para timbrado mientras el PSTN suena (SDK ya `active`) y cierre al colgar el remoto.
 */
export async function resolveDialContextAfterStartCall(options: {
  tenantId: string;
  body: { to_number?: string; lead_id?: string };
  /** Opcional; el número se escribe en `telnyxWebRTCStore` directamente (mismo que el FAB). */
  setDialContext?: (phone: string | null) => void;
  /** UUID de `crm_calls` devuelto por la Edge Function. */
  crmCallId?: string | null;
}): Promise<void> {
  const to = options.body.to_number?.trim();
  if (to) {
    syncWebRtcStoreFromOutboundCrmInsert({
      destinationPhone: to,
      crmCallId: options.crmCallId ?? null,
      crmStatus: 'initiated',
    });
    return;
  }
  const leadId = options.body.lead_id?.trim();
  if (!leadId) {
    return;
  }
  beginCallUiFromApp();
  if (options.crmCallId) {
    setActiveCrmCall(options.crmCallId, 'initiated', { forceBusinessOutbound: true });
  }
  const { data } = await supabase
    .from('crm_leads')
    .select('phone')
    .eq('tenant_id', options.tenantId)
    .eq('id', leadId)
    .maybeSingle();
  const phone = (data as { phone?: string | null } | null)?.phone?.trim();
  /** Mismo store que el FAB; `setDialContext` del hook equivale a esta llamada. */
  setDialContext(phone ?? null);
}

type CrmCallHydrateRow = {
  id: string;
  destination_phone?: string | null;
  call_status?: string | null;
  agent_id?: string | null;
};

/**
 * Cuando Realtime no está publicado en `crm_calls`, `client_state` no llega en el SDK, o hay
 * desajuste `agent_id` vs usuario logueado: sondeo idempotente mientras la UI está en llamada.
 */
const ACTIVE_CALL_STATUSES = ['pending', 'initiated', 'ringing', 'answered'] as const;

function isWebRtcInActiveLeg(s: TelnyxWebRTCState): boolean {
  if (s.callPhase !== 'none') return true;
  const id = s.activeCallId;
  return id != null && id !== '' && id !== 'pending';
}

export async function tryHydrateOutboundCrmCallFromDb(params: {
  tenantId: string;
  userId: string;
}): Promise<void> {
  const { tenantId, userId } = params;
  const s = getWebRTCState();
  if (s.activeCrmCallId?.trim()) return;
  /** Sin esto, un tick puede ejecutarse antes de que React refleje `callPhase === 'ringing'`. */
  if (!isWebRtcInActiveLeg(s)) return;

  const sinceWide = new Date(Date.now() - 180_000).toISOString();
  const sinceNarrow = new Date(Date.now() - 120_000).toISOString();

  const pickAndSync = (row: CrmCallHydrateRow | null | undefined) => {
    if (!row?.id) return;
    const dest = row.destination_phone?.trim() || null;
    syncWebRtcStoreFromOutboundCrmInsert({
      destinationPhone: dest,
      crmCallId: row.id,
      crmStatus: row.call_status ?? 'initiated',
    });
    if (!dest) {
      void supabase
        .from('crm_calls')
        .select('destination_phone')
        .eq('id', row.id)
        .eq('tenant_id', tenantId)
        .maybeSingle()
        .then(({ data }) => {
          const p = (data as { destination_phone?: string | null } | null)?.destination_phone?.trim();
          if (p) mergeOutboundDestinationPhone(p);
        });
    }
  };

  const { data: agentRow, error: errAgent } = await supabase
    .from('crm_calls')
    .select('id, destination_phone, call_status')
    .eq('tenant_id', tenantId)
    .eq('agent_id', userId)
    .in('call_status', [...ACTIVE_CALL_STATUSES])
    .gte('created_at', sinceWide)
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle();

  if (!errAgent && agentRow?.id) {
    pickAndSync(agentRow as CrmCallHydrateRow);
    return;
  }

  const { data: candidates, error: errCand } = await supabase
    .from('crm_calls')
    .select('id, destination_phone, call_status, agent_id')
    .eq('tenant_id', tenantId)
    .in('call_status', [...ACTIVE_CALL_STATUSES])
    .gte('created_at', sinceNarrow)
    .order('created_at', { ascending: false })
    .limit(5);

  if (!errCand && candidates?.length) {
    const mine = candidates.find((r) => (r as CrmCallHydrateRow).agent_id === userId);
    if (mine) {
      pickAndSync(mine as CrmCallHydrateRow);
      return;
    }
    if (candidates.length === 1) {
      pickAndSync(candidates[0] as CrmCallHydrateRow);
      return;
    }
  }

  /**
   * Muchas filas “colgadas” en pruebas: `length > 1` y ninguna coincide con `userId`.
   * Si en la ventana solo existe **una** fila activa en todo el tenant, es la llamada actual.
   */
  const { count, error: cntErr } = await supabase
    .from('crm_calls')
    .select('id', { count: 'exact', head: true })
    .eq('tenant_id', tenantId)
    .in('call_status', [...ACTIVE_CALL_STATUSES])
    .gte('created_at', sinceNarrow);

  if (!cntErr && count === 1) {
    const { data: lone, error: loneErr } = await supabase
      .from('crm_calls')
      .select('id, destination_phone, call_status, agent_id')
      .eq('tenant_id', tenantId)
      .in('call_status', [...ACTIVE_CALL_STATUSES])
      .gte('created_at', sinceNarrow)
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();
    if (!loneErr && lone?.id) pickAndSync(lone as CrmCallHydrateRow);
  }
}
